import React, { useEffect, useReducer, useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import { makeStyles } from '@fluentui/react-components';
import { getQuestionById, getQuestionsIdsByExam } from '../../services/apiService';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Card,
  Text,
  Button,
  Image,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  ProgressBar,
} from '@fluentui/react-components';
import { 
  FaChevronLeft, 
  FaChevronRight,
  FaClock,
  FaList,
  FaCheck,
  FaTimes,
} from 'react-icons/fa';

const useStyles = makeStyles({
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    padding: '16px 24px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  },
  timer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#323130',
    fontSize: '24px',
    fontWeight: '600',
  },
  timerIcon: {
    color: '#0078d4',
    fontSize: '24px',
  },
  navigation: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  navigationButton: {
    backgroundColor: '#fff',
    border: '1px solid #0078d4',
    color: '#0078d4',
    padding: '8px 16px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f3f9fd',
    },
    '&:disabled': {
      borderColor: '#c8c8c8',
      color: '#c8c8c8',
      cursor: 'not-allowed',
    },
  },
  finishButton: {
    backgroundColor: '#107c10',
    color: '#fff',
    border: 'none',
    '&:hover': {
      backgroundColor: '#0b5a0b',
    },
  },
  questionCard: {
    padding: '32px',
    backgroundColor: '#fff',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    marginBottom: '24px',
    border: '1px solid #0078d4',
  },
  questionText: {
    fontSize: '18px',
    color: '#323130',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  mediaContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  optionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  option: {
    padding: '16px',
    borderRadius: '4px',
    border: '1px solid #edebe9',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#f3f9fd',
      borderColor: '#0078d4',
    },
  },
  optionSelected: {
    backgroundColor: '#f3f9fd',
    borderColor: '#0078d4',
  },
  optionCorrect: {
    backgroundColor: '#dff6dd',
    borderColor: '#107c10',
  },
  optionWrong: {
    backgroundColor: '#fde7e9',
    borderColor: '#d13438',
  },
  optionLabel: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#f3f2f1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '600',
  },
  questionProgress: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#605e5c',
  },
  progressIndicator: {
    height: '4px',
    backgroundColor: '#edebe9',
    borderRadius: '2px',
    flex: 1,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#0078d4',
    borderRadius: '2px',
    transition: 'width 0.3s ease',
  },
  questionNumbers: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
    gap: '8px',
    marginTop: '24px',
  },
  questionNumber: {
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid #edebe9',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#f3f9fd',
      borderColor: '#0078d4',
    },
  },
  questionNumberAnswered: {
    backgroundColor: '#f3f9fd',
    borderColor: '#0078d4',
  },
  questionNumberCorrect: {
    backgroundColor: '#dff6dd',
    borderColor: '#107c10',
  },
  questionNumberWrong: {
    backgroundColor: '#fde7e9',
    borderColor: '#d13438',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    gap: '16px',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #f3f2f1',
    borderTop: '4px solid #0078d4',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
});

const Questions = () => {
  const styles = useStyles();
  const { examId } = useParams();
  const { t } = useTranslation(['questions']);
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const [questionIds, setQuestionIds] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [statics, setStatics] = useState({
    correct: 0,
    wrong: 0,
    note: 0
  });
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [seconds, setSeconds] = useState(3599);
  const [isLoading, setIsLoading] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    document.title = t('headers:questions');
    fetchData();

    const intervalId = setInterval(() => {
      setSeconds(prevSeconds => {
        if (prevSeconds === 0) {
          clearInterval(intervalId); // Geri sayım tamamlandı, interval'i temizle
          return 0;
        } else {
          return prevSeconds - 1;
        }    
      });
    }, 1000)
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (questions[currentQuestionIndex]?.image && typeof questions[currentQuestionIndex]?.image === 'string' && questions[currentQuestionIndex]?.image.indexOf('data:video') === 0) {
      if (videoRef.current) {
        const savedVolume = localStorage.getItem('videoVolume');
        if (savedVolume !== null) {
          videoRef.current.volume = parseFloat(savedVolume);
        }
      }
    }
  }, [currentQuestionIndex, questions]);

  const fetchQuestion = async (questionId, index = null) => {
    const questionData = await getQuestionById(questionId);
    if(index === null) setQuestions((prevQuestions) => [...prevQuestions, questionData]);
    else setQuestions((prevQuestions) => {
      const newQuestions = [...prevQuestions];
      newQuestions[index] = questionData;
      return newQuestions;
    });
  }

  const fetchData = async () => {
    const questionsData = await getQuestionsIdsByExam(examId);
    setQuestionIds(questionsData);
    const question = await getQuestionById(questionsData[0].id);
    setQuestions((prevQuestions) => [...prevQuestions, question]);
    setIsLoading(false);
  }

  const fetchAllQuestions = async (questionData) => {
    questionData.forEach((question) => {
      fetchQuestion(question.id);
    });
    setIsLoading(false);
  }

  const handleOptionPress = (value, index) => {
    const newAnswers = [...answers];
    newAnswers[index] = value;
    setAnswers(newAnswers);
    setShowCorrectAnswers(true);
    forceUpdate();
  };

  const showAnswers = () => {
    let correctCounter = 0;
    let wrongCounter = 0;
    answers.forEach((answer, index) => {
      if (answer === questions[index].correctChoice) correctCounter++;
      else wrongCounter++;
    });
    setStatics({
      correct: correctCounter,
      wrong: wrongCounter,
      note: correctCounter * 2,
    });
    setModalVisible(true);
    setShowCorrectAnswers(true);
  };

  const handleClose = () => {
    setModalVisible(false);
  };

  const handleNextQuestion = async () => {
    if (currentQuestionIndex < questionIds.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestionId = questionIds[nextIndex].id;
      setCurrentQuestionIndex(nextIndex);
      setShowCorrectAnswers(false);
      if (!questions.some(q => q !== undefined && q.id === nextQuestionId)) {
        setIsLoading(true);
        await fetchQuestion(nextQuestionId, nextIndex);
        setIsLoading(false);
      }
    } else {
      setModalVisible(true);
    }
  };

  const handlePreviousQuestion = async () => {
    if (currentQuestionIndex > 0) {
      const previousIndex = currentQuestionIndex - 1;
      const previousQuestionId = questionIds[previousIndex].id;
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setShowCorrectAnswers(false);
      if (!questions.some(q => q !== undefined && q.id === previousQuestionId)) {
        setIsLoading(true);
        await fetchQuestion(previousQuestionId, previousIndex);
        setIsLoading(false);
      }
    }
  };

  const handleFormPress = async (index) => {
    setCurrentQuestionIndex(index)
    setShowCorrectAnswers(false)
    if(questions[index] === undefined) {
      setIsLoading(true)
      await fetchQuestion(questionIds[index].id, index)
      setIsLoading(false)
    }
  }

  const handleVolumeChange = () => {
    if (videoRef.current) {
      localStorage.setItem('videoVolume', videoRef.current.volume);
    }
  };

  const handleCopy = (event) => {
    event.preventDefault();
  };

  const getOptionName = (option) => {
    switch (option) {
      case 'A':
        return t('questions:A');
      case 'B':
        return t('questions:B');
      case 'C':
        return t('questions:C');
      case 'D':
        return t('questions:D');
      default:
        return '';
    }
  };

  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  const progress = ((currentQuestionIndex + 1) / questionIds.length) * 100;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.timer}>
          <FaClock className={styles.timerIcon} />
          {minutes.toString().padStart(2, '0')}:{remainingSeconds.toString().padStart(2, '0')}
        </div>
        <div className={styles.questionProgress}>
          <Text>Soru {currentQuestionIndex + 1}/{questionIds.length}</Text>
          <div className={styles.progressIndicator}>
            <div 
              className={styles.progressFill} 
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        <div className={styles.navigation}>
          <Button 
            className={styles.navigationButton}
            onClick={handlePreviousQuestion} 
            disabled={currentQuestionIndex === 0}
          >
            <FaChevronLeft />
            {t('questions:previous')}
          </Button>
          {currentQuestionIndex < questionIds.length - 1 ? (
            <Button 
              className={styles.navigationButton}
              onClick={handleNextQuestion}
              disabled={isLoading}
            >
              {t('questions:next')}
              <FaChevronRight />
            </Button>
          ) : (
            <Button 
              className={`${styles.navigationButton} ${styles.finishButton}`}
              onClick={handleNextQuestion}
            >
              {t('questions:finish')}
              <FaCheck />
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <Text size={400}>{t('dashboard:loading')}</Text>
        </div>
      ) : questions.length > 0 && questions[currentQuestionIndex] !== undefined && (
        <Card className={styles.questionCard}>
          {questions[currentQuestionIndex]?.image && (
            <div className={styles.mediaContainer}>
              {questions[currentQuestionIndex]?.image.indexOf('data:image') === 0 ? (
                <Image 
                  src={questions[currentQuestionIndex]?.image} 
                  alt="Soru Görseli"
                  style={{ maxHeight: 250 }}
                />
              ) : (
                <video
                  ref={videoRef}
                  controls
                  width="640"
                  height="360"
                  autoPlay
                  controlsList="nodownload"
                  onVolumeChange={handleVolumeChange}
                >
                  <source src={questions[currentQuestionIndex]?.image} type="video/mp4" />
                  Tarayıcınız video etiketini desteklemiyor.
                </video>
              )}
            </div>
          )}
          
          <div 
            className={styles.questionText}
            onCopy={handleCopy} 
            style={{ userSelect: 'none' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(questions[currentQuestionIndex]?.questionText || '') }}
          />

          <div className={styles.optionsContainer}>
            {['A', 'B', 'C', 'D'].map((option) => {
              const isSelected = answers[currentQuestionIndex] === option;
              const isCorrect = questions[currentQuestionIndex]?.correctChoice === option;
              const isWrong = isSelected && !isCorrect && showCorrectAnswers;
              
              let optionClassName = styles.option;
              if (isSelected) optionClassName += ` ${styles.optionSelected}`;
              if (showCorrectAnswers && isCorrect) optionClassName += ` ${styles.optionCorrect}`;
              if (isWrong) optionClassName += ` ${styles.optionWrong}`;

              return (
                <div
                  key={option}
                  className={optionClassName}
                  onClick={() => handleOptionPress(option, currentQuestionIndex)}
                >
                  <span className={styles.optionLabel}>{option}</span>
                  {questions[currentQuestionIndex][`option${option}`]?.indexOf('data:image') === 0 ? (
                    <Image 
                      src={questions[currentQuestionIndex][`option${option}`]} 
                      alt={`Seçenek ${option}`}
                      style={{ height: 140 }}
                    />
                  ) : (
                    <Text>
                      {questions[currentQuestionIndex][`option${option}`]}
                    </Text>
                  )}
                  {showCorrectAnswers && isCorrect && <FaCheck style={{ color: '#107c10' }} />}
                  {isWrong && <FaTimes style={{ color: '#d13438' }} />}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className={styles.questionNumbers}>
        {questionIds.map((question, index) => {
          let className = styles.questionNumber;
          if (questions[index] !== undefined) {
            if (answers[index] === questions[index]?.correctChoice) {
              className += ` ${styles.questionNumberCorrect}`;
            } else if (answers[index]) {
              className += ` ${styles.questionNumberWrong}`;
            } else if (answers[index]) {
              className += ` ${styles.questionNumberAnswered}`;
            }
          }

          return (
            <div
              key={question.id}
              className={className}
              onClick={() => handleFormPress(index)}
            >
              {index + 1}
            </div>
          );
        })}
      </div>

      <Dialog open={modalVisible}>
        <DialogSurface style={{ 
          backgroundColor: '#fff',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
        }}>
          <DialogBody>
            <DialogContent>
              <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <Text size={500} weight="semibold">Sınav Sonucu</Text>
                <div style={{ marginTop: '16px' }}>
                  <Text>Doğru: {statics.correct}</Text>
                  <Text>Yanlış: {statics.wrong}</Text>
                  <Text>Puan: {statics.note}</Text>
                </div>
              </div>
              <div className={styles.questionNumbers}>
                {questionIds.map((question, index) => {
                  let className = styles.questionNumber;
                  if (questions[index] !== undefined) {
                    if (answers[index] === questions[index]?.correctChoice) {
                      className += ` ${styles.questionNumberCorrect}`;
                    } else if (answers[index]) {
                      className += ` ${styles.questionNumberWrong}`;
                    }
                  }

                  return (
                    <div
                      key={question.id}
                      className={className}
                      onClick={() => {
                        setCurrentQuestionIndex(index);
                        setModalVisible(false);
                      }}
                    >
                      {index + 1}
                    </div>
                  );
                })}
              </div>
            </DialogContent>
            <DialogActions>
              <Button 
                appearance="primary"
                onClick={handleClose}
              >
                {t('questions:review')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Questions;
